'use client';

import { Button, Toast, useToast } from '@zillow/constellation-10';
import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useCurrentUser } from '@/src/lib/hooks/query';
// import { ListingCard } from '@/src/components/ListingCard';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { getContactFormClickstreamContext } from '@/src/components/features/property/lib/analytics';
import { isWaitlistedListing } from '@/src/components/features/property/lib/waitlist';
import { emitTrackModalContactFormViewEvent } from '@/src/analytics/hotpads/events';
import { ContactForm } from './ContactForm';
import { useHdpContactSubmission } from './useHdpContactSubmission';
import { getActivePostContactFlows, postContactFlowShowsSuccessConfirmation } from './postContactFlowRegistry';
import type {
  ContactClickstreamContext,
  ContactFormHostMode,
  ContactFormInputValues,
  ContactFormMode,
  ContactSubmissionContext,
} from './types';
import { spokeContainer, spokeContent, successBody, successContainer, successTitle } from './contactFormSpoke.styles';

type ContactFormSpokeProps = {
  onClose: () => void;
  listing?: PropertyDetailListing | null;
  mode?: ContactFormMode;
  /** Forwarded to ContactForm so multiple instances on the page (modal + inline) don't collide on element ids. */
  idPrefix?: string;
  /** "modal" (default) or "inline" — drives the clickstream `triggerObject` for submissions. */
  formMode?: ContactFormHostMode;
  clickstreamContext?: ContactClickstreamContext;
  onSubmitSuccess?: (context: ContactSubmissionContext) => void;
};

/**
 * ContactFormSpoke — fullscreen-overlay body for the contact flow.
 *
 * Runs the App Router HDP contact pipeline: validate contact info, freepass
 * auth when needed, submit a real inquiry, then hand post-contact modal
 * context back to the host surface.
 */
export const ContactFormSpoke = ({
  onClose,
  listing,
  mode = 'contact',
  idPrefix,
  formMode = 'modal',
  clickstreamContext,
  onSubmitSuccess,
}: ContactFormSpokeProps) => {
  const t = useTranslations('contactForm');
  const { data: user } = useCurrentUser();
  const { enqueueToast } = useToast();
  const [isSuccess, setIsSuccess] = useState(false);
  const { inquiryMessageCache, isLoading, serverErrors, submitContact } = useHdpContactSubmission({
    listing,
    mode,
    formMode,
    clickstreamContext,
  });
  const { triggerLocation: modalImpressionTriggerLocation, triggerObject: modalImpressionTriggerObject } =
    getContactFormClickstreamContext(clickstreamContext, 'impression', formMode);

  useEffect(() => {
    if (formMode === 'inline') {
      return;
    }

    emitTrackModalContactFormViewEvent({
      listing,
      triggerLocation: modalImpressionTriggerLocation,
      triggerObject: modalImpressionTriggerObject,
    });
  }, [formMode, listing, modalImpressionTriggerLocation, modalImpressionTriggerObject]);

  const handleSubmit = async (values: ContactFormInputValues) => {
    const context = await submitContact(values);
    if (!context) {
      return;
    }

    if (onSubmitSuccess) {
      const activeFlows = getActivePostContactFlows(context);
      const hasVisibleSuccessConfirmation = activeFlows.some(postContactFlowShowsSuccessConfirmation);
      if (context.mode === 'contact' && !hasVisibleSuccessConfirmation) {
        enqueueToast(
          <Toast tone="success" role="status">
            {t('successToast')}
          </Toast>,
        );
      }

      onSubmitSuccess(context);
    } else {
      setIsSuccess(true);
    }
  };

  if (isSuccess) {
    const successTitleText =
      mode === 'tour'
        ? t('successTitleTour')
        : isWaitlistedListing(listing)
          ? t('successTitleWaitlist')
          : t('successTitleContact');

    return (
      <div className={spokeContainer}>
        <div className={successContainer} role="status" aria-live="polite">
          <h3 className={successTitle}>{successTitleText}</h3>
          <p className={successBody}>{t('successBody')}</p>
          <Button tone="brand" emphasis="filled" onClick={onClose}>
            {t('done')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={spokeContainer}>
      <div className={spokeContent}>
        {/* {listing && <ListingCard listing={listing} appearance="horizontal" readOnly />} */}
        <ContactForm
          listing={listing ?? null}
          onSubmit={handleSubmit}
          isLoading={isLoading}
          serverErrors={serverErrors}
          userInquiryDataCache={inquiryMessageCache ?? undefined}
          user={
            user
              ? {
                  loggedIn: user.loggedIn,
                  info: {
                    firstName: user.data?.firstName,
                    lastName: user.data?.lastName,
                    email: user.data?.email,
                  },
                }
              : undefined
          }
          mode={mode}
          idPrefix={idPrefix}
        />
      </div>
    </div>
  );
};
